/**
 * Global multiplexed conversations WebSocket client (iter135.12).
 *
 * One persistent socket per browser tab that subscribes to EVERY
 * conversation the authed user is a member of. Mounted near the
 * top of the React tree (in App.js as a sibling of PresenterProvider)
 * so it survives route navigation — when the user moves to /us-class
 * and back, no reconnect, no missed events.
 *
 * Replaces the per-conversation ``useConvSocket`` from iter125.
 *
 * Inbound (server → client):
 *   • {type: "subscribed", conv_ids: [...]} — sent once on open.
 *   • {type: "pong"} — keepalive ack.
 *   • {type: "message_new" | "message_edited" | "message_recalled"
 *      | "typing" | <other>, conv_id: <cid>, ...} — fanout from any
 *      subscribed conv. The page filters by conv_id at the listener
 *      level so the sidebar can react globally while the message
 *      list only reacts to the open conv.
 *
 * Outbound (client → server):
 *   • {type: "ping"} — every 25 s to keep the proxy from idle-timing
 *     out the WS. Mirrors the iter135.9 keepalive on the share WS.
 *   • {type: "typing", conv_id, is_typing} — replaces
 *     POST /typing on every keystroke debounce.
 *   • {type: "read", conv_id, up_to_seq} — replaces POST /read.
 *
 * Reconnect: exponential backoff capped at 30 s. The store's
 * version counters absorb any duplicate broadcasts that land
 * post-reconnect — ``appendMessage`` is idempotent.
 */
import { useEffect, useState } from "react";

const _resolveWsBase = () => {
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const wsBase = apiBase.replace(/^https?:\/\//, (m) =>
    m === "https://" ? "wss://" : "ws://",
  );
  return `${wsBase}/api/conv/ws/global`;
};

class GlobalConvSocket {
  constructor() {
    this.ws = null;
    this.subscribedConvIds = new Set();
    /** Set<(msg) => void> */
    this.listeners = new Set();
    /** Set<(connected: boolean) => void> */
    this.statusListeners = new Set();
    this._email = null;
    this._sessionToken = null;
    this._pingTimer = null;
    this._reconnectTimer = null;
    this._backoffMs = 500;
    this._connected = false;
    this._stopped = false;
    /** Buffered outbound frames sent before the WS opens. Drained
     *  once the connection is established. Bounded so a long-offline
     *  user doesn't accumulate megabytes of backlog. */
    this._outboundBuffer = [];
  }

  // ── Lifecycle ────────────────────────────────────────────────

  start({ email, sessionToken } = {}) {
    // Idempotent — if we're already running for the same identity,
    // do nothing. ``user.email`` triggers a re-run on every parent
    // re-render, so without this guard we'd churn the WS.
    if (
      !this._stopped && this.ws &&
      (this._email || null) === (email || null) &&
      (this._sessionToken || null) === (sessionToken || null)
    ) {
      return;
    }
    // Identity changed (or first start) — tear down and reconnect.
    if (this.ws) this.stop();
    this._email = email || null;
    this._sessionToken = sessionToken || null;
    this._stopped = false;
    if (!this._email && !this._sessionToken) return;
    this._connect();
  }

  stop() {
    this._stopped = true;
    if (this._reconnectTimer) clearTimeout(this._reconnectTimer);
    if (this._pingTimer) clearInterval(this._pingTimer);
    this._reconnectTimer = null;
    this._pingTimer = null;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try { this.ws.close(); } catch { /* noop */ }
    }
    this.ws = null;
    this._setConnected(false);
  }

  _connect() {
    if (this._stopped) return;
    let url = _resolveWsBase();
    const params = new URLSearchParams();
    if (this._sessionToken) params.set("session", this._sessionToken);
    else if (this._email) params.set("email", this._email);
    if (params.toString()) url += `?${params}`;

    let ws;
    try { ws = new WebSocket(url); }
    catch { this._scheduleReconnect(); return; }
    this.ws = ws;

    ws.addEventListener("open", () => {
      this._backoffMs = 500;
      this._setConnected(true);
      // Drain any frames that were buffered while we were offline.
      const pending = this._outboundBuffer.splice(0);
      for (const frame of pending) {
        try { ws.send(JSON.stringify(frame)); } catch { /* noop */ }
      }
      // Start the 25 s keepalive ping.
      if (this._pingTimer) clearInterval(this._pingTimer);
      this._pingTimer = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          try { ws.send(JSON.stringify({ type: "ping" })); } catch { /* noop */ }
        }
      }, 25000);
    });

    ws.addEventListener("message", (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (!msg || typeof msg !== "object") return;
      if (msg.type === "subscribed") {
        this.subscribedConvIds = new Set(msg.conv_ids || []);
        // Forward to listeners too — the page can use this signal
        // to refresh its sidebar list once on connect.
      }
      // Ignore pong; everything else fans out.
      if (msg.type === "pong") return;
      this.listeners.forEach((fn) => {
        try { fn(msg); } catch { /* never let one bad consumer break realtime */ }
      });
    });

    ws.addEventListener("close", () => {
      if (this._pingTimer) clearInterval(this._pingTimer);
      this._pingTimer = null;
      this._setConnected(false);
      this.ws = null;
      this._scheduleReconnect();
    });

    ws.addEventListener("error", () => {
      // ``close`` will follow — schedule reconnect there.
    });
  }

  _scheduleReconnect() {
    if (this._stopped) return;
    if (this._reconnectTimer) return;
    const delay = this._backoffMs;
    this._backoffMs = Math.min(30000, this._backoffMs * 2);
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      this._connect();
    }, delay);
  }

  _setConnected(v) {
    if (this._connected === v) return;
    this._connected = v;
    this.statusListeners.forEach((fn) => {
      try { fn(v); } catch { /* noop */ }
    });
  }

  // ── Public API ──────────────────────────────────────────────

  /** Subscribe to ALL inbound events. Returns an unsubscribe fn. */
  on(handler) {
    this.listeners.add(handler);
    return () => this.listeners.delete(handler);
  }

  onStatus(handler) {
    this.statusListeners.add(handler);
    // Fire immediately with the current state.
    try { handler(this._connected); } catch { /* noop */ }
    return () => this.statusListeners.delete(handler);
  }

  isConnected() { return this._connected; }

  isSubscribedTo(convId) { return this.subscribedConvIds.has(convId); }

  send(frame) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try { this.ws.send(JSON.stringify(frame)); }
      catch { /* fall through to buffer */ }
      return;
    }
    // Buffer up to ~32 frames so a brief disconnect doesn't lose
    // a typing tick or a mark-read. Drop oldest when full.
    if (this._outboundBuffer.length >= 32) this._outboundBuffer.shift();
    this._outboundBuffer.push(frame);
  }

  sendTyping(convId, isTyping) {
    if (!convId) return;
    this.send({ type: "typing", conv_id: convId, is_typing: !!isTyping });
  }

  sendRead(convId, upToSeq) {
    if (!convId || !upToSeq) return;
    this.send({ type: "read", conv_id: convId, up_to_seq: Number(upToSeq) || 0 });
  }
}

export const convSocket = new GlobalConvSocket();

// ── React hook ──────────────────────────────────────────────────

/** Subscribe a component to the global conv socket.
 *
 *  ``onEvent`` is called for every inbound fanout frame. The page
 *  filters by ``conv_id`` at the listener level so the sidebar can
 *  react globally while the message list only reacts to the open
 *  conv. Returns ``{connected}`` so the page can show a "you're
 *  offline" banner when the WS is disconnected.
 *
 *  The hook does NOT call ``convSocket.start`` — that's owned by
 *  App.js so the socket survives route changes. The hook only
 *  attaches its listener.
 */
export function useGlobalConvSocket(onEvent) {
  const [connected, setConnected] = useState(() => convSocket.isConnected());
  useEffect(() => {
    const offEv = onEvent ? convSocket.on(onEvent) : () => {};
    const offSt = convSocket.onStatus(setConnected);
    return () => { offEv(); offSt(); };
  }, [onEvent]);
  return { connected };
}

// Dev-only console handle.
if (typeof window !== "undefined") {
  window.__convSocket = convSocket;  // eslint-disable-line no-underscore-dangle
}
